import { call, put, takeLatest, delay } from 'redux-saga/effects';
import axios from 'axios';
import config from '../../../config';
import { 
  REQUEST, 
  editModelSuccess, 
  editModelFailure 
} from '../../actions/models/edit';
import { fetchRequest as fetchModels } from '../../actions/models/list';
import { fetchRequest as fetchModelDetail } from '../../actions/models/detail';
import { getLocalUser } from '../../../utils/localUser';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';
import { Error as ErrorAction } from '../../actions/error/error';

// Worker saga for editing a model
function* editModel(action) {
  try {
    // Get headers from the utility function - consistent with other sagas
    const headers = getLocalUser();
    
    if (!headers.Authorization) {
      throw new Error('Authentication token not found');
    }
    
    // Make sure we have workspace ID for proper filtering
    if (!headers['X-WORKSPACE-ID'] && action.payload.workspace_id) {
      headers['X-WORKSPACE-ID'] = action.payload.workspace_id;
    }
    
    // Add content type header
    headers['Content-Type'] = 'application/json';
    
    const response = yield call(axios.put, 
      `${config.API_URL}/pipelines/${action.modelId}`,
      action.payload,
      { headers }
    );
    
    // First dispatch success action with the response data
    yield put(editModelSuccess(response.data));
    
    // Check if this is just toggling active state (pause/resume training)
    const isToggleOperation = Object.keys(action.payload).length === 1 && 'active' in action.payload;
    
    // Add success snackbar message with appropriate text
    if (isToggleOperation) {
      const message = action.payload.active ? 'Model training resumed' : 'Model training paused';
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message });
    } else {
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: 'Pipeline updated successfully' });
    }
    
    // Add a short delay to ensure the UI has time to process the success
    yield delay(300);
    
    // For toggle operations, we don't need to refresh the full models list
    // as this causes unnecessary API calls and potential errors
    if (!isToggleOperation) {
      // Then refresh the models list after successful edit
      yield put(fetchModels({ 
        workspace_id: action.payload.workspace_id || localStorage.getItem('WORKSPACE_ID'),
        page: 1,
        per_page: 10
      }));
    }
    
    // Always refresh the model detail
    yield put(fetchModelDetail(action.modelId));
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Edit model saga error:', error);

    const errorMessage = error?.response?.status === 404
      ? `Model with ID ${action.modelId} could not be found or endpoint doesn't exist`
      : extractErrorMessage(error);

    // Dispatch global error action like other sagas
    yield put(ErrorAction(error));
    
    // Dispatch failure action with the error
    yield put(editModelFailure(errorMessage));
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

// Watcher saga for model editing
export function* watch() {
  yield takeLatest(REQUEST, editModel);
} 